import React, { useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FileSpreadsheet, Trash2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { API, errorText } from '../governance_v2/constants';

const SOURCE = { manual: 'Manual', excel: 'Excel', vector_bridge: 'Vector Bridge' };

const useInvalidate = (companyId) => {
  const qc = useQueryClient();
  return () => { qc.invalidateQueries({ queryKey: ['budgets', companyId] }); qc.invalidateQueries({ queryKey: ['historical', companyId] }); };
};

const ManualForm = ({ companyId }) => {
  const refresh = useInvalidate(companyId);
  const [f, setF] = useState({ kpi_name: '', period: '', amount: '' });
  const save = useMutation({
    mutationFn: () => axios.put(`${API}/companies/${companyId}/budgets`, { items: [{ ...f, amount: Number(f.amount) }] }),
    onSuccess: () => { toast.success('Presupuesto guardado'); setF({ ...f, amount: '' }); refresh(); },
    onError: (e) => toast.error(errorText(e)),
  });
  return (
    <div className="grid grid-cols-[2fr_1fr_1fr_auto] gap-2" data-testid="budget-manual-form">
      <Input placeholder="KPI (igual que en Mission Control)" value={f.kpi_name} onChange={(e) => setF({ ...f, kpi_name: e.target.value })} data-testid="budget-kpi-input" />
      <Input type="month" value={f.period} onChange={(e) => setF({ ...f, period: e.target.value })} data-testid="budget-period-input" />
      <Input type="number" placeholder="Presupuesto" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} data-testid="budget-amount-input" />
      <Button onClick={() => save.mutate()} disabled={f.kpi_name.trim().length < 2 || !f.period || f.amount === '' || save.isPending} data-testid="budget-save-btn">Guardar</Button>
    </div>
  );
};

const ExcelUpload = ({ companyId }) => {
  const refresh = useInvalidate(companyId);
  const ref = useRef(null);
  const up = useMutation({
    mutationFn: (file) => { const fd = new FormData(); fd.append('file', file); return axios.post(`${API}/companies/${companyId}/budgets/upload`, fd); },
    onSuccess: (r) => { toast.success(`${r.data.saved} presupuestos cargados${r.data.errors.length ? ` · ${r.data.errors.length} filas con error` : ''}`); refresh(); },
    onError: (e) => toast.error(errorText(e)),
  });
  return (
    <div className="flex flex-wrap items-center gap-3 rounded-lg border border-dashed border-slate-300 p-3 dark:border-slate-600">
      <FileSpreadsheet className="h-5 w-5 text-emerald-600" />
      <p className="mr-auto text-xs text-slate-500">Excel con columnas <b>KPI, Mes, Presupuesto</b> (Módulo opcional), o una columna por mes (AAAA-MM).</p>
      <input ref={ref} type="file" accept=".xlsx" className="hidden" onChange={(e) => e.target.files[0] && up.mutate(e.target.files[0])} data-testid="budget-excel-input" />
      <Button size="sm" variant="outline" onClick={() => ref.current?.click()} disabled={up.isPending} data-testid="budget-excel-btn">{up.isPending ? 'Cargando…' : 'Subir Excel'}</Button>
    </div>
  );
};

export const BudgetManagerDialog = ({ companyId, onClose }) => {
  const refresh = useInvalidate(companyId);
  const { data } = useQuery({ queryKey: ['budgets', companyId], queryFn: async () => (await axios.get(`${API}/companies/${companyId}/budgets`)).data.budgets });
  const del = useMutation({
    mutationFn: (id) => axios.delete(`${API}/companies/${companyId}/budgets/${id}`),
    onSuccess: () => { toast.success('Presupuesto eliminado'); refresh(); },
    onError: (e) => toast.error(errorText(e)),
  });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[85vh] max-w-3xl overflow-y-auto" data-testid="budget-manager-dialog">
        <DialogHeader><DialogTitle>Presupuestos por KPI y mes</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <ManualForm companyId={companyId} />
          <ExcelUpload companyId={companyId} />
          <p className="text-xs text-slate-500">Vector Bridge: <code>POST /api/external/budgets</code> con <code>{'{"items": [{"kpi_name", "period": "AAAA-MM", "amount"}]}'}</code></p>
          <table className="w-full text-sm" data-testid="budget-list">
            <thead className="text-left text-xs uppercase text-slate-500"><tr><th className="p-2">KPI</th><th className="p-2">Mes</th><th className="p-2">Presupuesto</th><th className="p-2">Origen</th><th /></tr></thead>
            <tbody>{(data || []).map((b) => (
              <tr key={b.id} className="border-t border-slate-200 dark:border-slate-700" data-testid={`budget-row-${b.id}`}>
                <td className="p-2">{b.kpi_name}</td><td className="p-2">{b.period}</td><td className="p-2">{Number(b.amount).toLocaleString('es')}</td>
                <td className="p-2 text-xs text-slate-500">{SOURCE[b.source] || b.source}</td>
                <td className="p-2"><Button size="icon" variant="ghost" onClick={() => del.mutate(b.id)} data-testid={`budget-delete-${b.id}`}><Trash2 className="h-4 w-4" /></Button></td>
              </tr>))}</tbody>
          </table>
          {!(data || []).length && <p className="text-center text-sm text-slate-400">Sin presupuestos cargados.</p>}
        </div>
      </DialogContent>
    </Dialog>
  );
};
